import { customFetch } from '@/lib/hiring-api/custom-fetch';
import { logCreate } from '@/lib/audit-log';
import { createCreatorLoginLink } from '@/lib/workerApi';

/* ============================================================================
 * Admin "switch as user" (impersonation)
 * ============================================================================
 * One place for the mechanism, shared by every listing that offers it: brands,
 * agencies, and the team members of either.
 *
 * POST /api/v1/admin/impersonate is gated on the acting admin's own Supabase JWT
 * (app_metadata.is_admin) and mints a one-time magic link server-side with the
 * service role. It resolves the canonical auth email from `userId` — so a stale
 * profiles.email can never misdirect the link — and returns a URL into the brand
 * dashboard's /auth/callback, which establishes the session via verifyOtp. The
 * acting admin's id/email ride along in that URL so the dashboard can tag
 * impersonated actions in its own audit log.
 *
 * `role` only tells the API which portal is meant: 'brand' and 'agency' both
 * land in the brand dashboard, and creators are refused by that API. Creators
 * go through startCreatorImpersonation below instead.
 *
 * Team members are ordinary Supabase auth users, so switching into one is the
 * same call as switching into an owner — pass their user_id. What they can see
 * once they are in is decided entirely by their own brand_user_access rows, not
 * by anything here.
 * ========================================================================== */

export type ImpersonationRole = 'brand' | 'agency';

export interface ImpersonationTarget {
  /** Supabase auth user id of the person to sign in as. */
  userId: string;
  /** Convenience only — the API resolves the authoritative email from userId. */
  email?: string | null;
  /** Which portal is meant. Both land in the brand dashboard. */
  role: ImpersonationRole;
  /** Audit table this switch is recorded against. */
  auditTable: string;
  /** Extra fields recorded alongside the email on the audit row. */
  auditValues?: Record<string, any>;
}

/**
 * Mint a one-time login link for `userId`, record the switch in the admin audit
 * trail, and open the dashboard signed in as them in a new tab.
 *
 * Returns the email the link was actually issued for (the API's resolved value,
 * which is what the caller should name in its confirmation toast). Throws on
 * failure so the caller can surface the message.
 */
export async function startImpersonation(target: ImpersonationTarget): Promise<string> {
  const res: any = await customFetch('/api/v1/admin/impersonate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ userId: target.userId, email: target.email, role: target.role }),
    responseType: 'json',
  });

  const actionLink = res?.actionLink;
  if (!actionLink) throw new Error('No login link was returned');

  const resolvedEmail: string = res?.email || target.email || '';

  // Audit trail: which admin switched into whom. Fire-and-forget — a failed log
  // write must not block the switch itself.
  logCreate(
    target.auditTable,
    target.userId,
    { email: resolvedEmail, ...(target.auditValues ?? {}) },
    { action: 'switch_as_user', triggered_by: 'admin' }
  );

  window.open(actionLink, '_blank', 'noopener,noreferrer');
  return resolvedEmail;
}

/**
 * Log in as a creator, in a new tab on the creator web portal (which lands on
 * /coach, as when the creator signs in themselves). Any creator, with or
 * without a Coach seat.
 *
 * Unlike startImpersonation above, this goes through the Brand-Dashboard
 * worker (POST /api/admin/creators/:id/portal-login), not the roster server
 * that is being retired. The worker checks the admin's JWT, refuses non-creator
 * and admin accounts, and returns a one-time portal sign-in URL.
 *
 * Recorded in the admin audit trail on success and on failure. Returns the
 * email the link was issued for; throws on failure.
 */
export async function startCreatorImpersonation(userId: string, name?: string | null): Promise<string> {
  try {
    const { url, email } = await createCreatorLoginLink(userId);
    logCreate('creator_impersonation', userId, { email, name: name ?? null }, {
      action: 'switch_as_user',
      triggered_by: 'admin',
      description: `Admin logged in as creator ${name || email} on the creator web portal`,
      status: 'success',
    });
    window.open(url, '_blank', 'noopener,noreferrer');
    return email;
  } catch (e: any) {
    logCreate('creator_impersonation', userId, { name: name ?? null }, {
      action: 'switch_as_user',
      triggered_by: 'admin',
      description: 'Failed to log in as creator on the creator web portal',
      status: 'failed',
      error: e?.message ?? 'Unknown error',
    });
    throw e;
  }
}
